import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "semantic-ui-react";
import ModalVideoTutorial from "react-lib/apps/MEH/ModalVideoTutorial";
import type { TabItem, TabPentaProps, TabPentaRef } from "./TabPenta";

export type TabMenuGroup = {
  id: string;
  label: React.ReactNode;
  shortLabel?: React.ReactNode;
  icon?: React.ReactNode;
  menuKeys: readonly string[];
};

type IndexedItem = { item: TabItem; sourceIndex: number };
type VisibleMenuGroup = Pick<TabMenuGroup, "id" | "label" | "shortLabel" | "icon"> & { items: IndexedItem[] };
type MenuTooltip = { kind: "item" | "group"; key: string; top: number; left: number };

export function groupTabItems(
  data: TabItem[], groups: readonly TabMenuGroup[], ungroupedLabel: React.ReactNode,
  ungroupedOptions: Pick<TabMenuGroup, "shortLabel" | "icon"> = {}
): VisibleMenuGroup[] {
  const byKey = new Map(data.map((item, sourceIndex) => [item.key, { item, sourceIndex }]));
  const used = new Set<string>();
  const result: VisibleMenuGroup[] = groups.map((group) => {
    const items: IndexedItem[] = [];
    group.menuKeys.forEach((key) => {
      const entry = byKey.get(key);
      if (entry && !used.has(key)) {
        used.add(key);
        items.push(entry);
      }
    });
    return { id: group.id, label: group.label, shortLabel: group.shortLabel, icon: group.icon, items };
  }).filter((group) => group.items.length > 0);
  const remaining = data.map((item, sourceIndex) => ({ item, sourceIndex }))
    .filter(({ item }) => !used.has(item.key));
  if (remaining.length) result.push({ id: "ungrouped", label: ungroupedLabel, ...ungroupedOptions, items: remaining });
  return result;
}

function textOf(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) return textOf((node.props as { children?: React.ReactNode }).children);
  return "";
}

// The visible short label must be part of the accessible name so voice control can target it.
function groupAccessibleName(group: VisibleMenuGroup, collapsed: boolean) {
  const fullName = textOf(group.label) || group.id;
  const shortName = collapsed ? textOf(group.shortLabel).trim() : "";
  return !shortName || fullName.toLowerCase().includes(shortName.toLowerCase()) ? fullName : `${shortName} ${fullName}`;
}

function isKeyboardFocus(element: Element) {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
}

let nextInstanceId = 0;

const RESIZE_HANDLE_WIDTH = 8;
const COLLAPSED_MENU_WIDTH = 80;
const EMPTY_DATA: TabItem[] = [];

type TabPentaContentProps = Pick<TabPentaProps, "renderEmpty"> & { data: TabItem[]; activeIndex: number };

// Sidebar-only state (drag, hover, tooltip, measured width) must not re-render the open page.
const TabPentaContent = React.memo(({ data, activeIndex, renderEmpty }: TabPentaContentProps) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    {data.map((item, index) => item.keepMounted ? <div key={item.key} style={index === activeIndex ? { height: "100%" } : { display: "none" }}>{item.render()}</div> : null)}
    {!data[activeIndex]?.keepMounted && (data[activeIndex]?.render() ?? null)}
    {data.length === 0 && renderEmpty}
  </div>
));
TabPentaContent.displayName = "TabPentaContent";

export function getMenuWidthBounds(containerWidth: number) {
  const width = Math.max(0, containerWidth);
  const contentReserve = Math.min(320, width / 2);
  const max = Math.max(0, Math.min(600, width * 0.6, width - RESIZE_HANDLE_WIDTH - contentReserve));
  return { min: Math.min(200, width * 0.4, max), max };
}

function clampMenuWidth(width: number, bounds: { min: number; max: number }) {
  return Math.max(bounds.min, Math.min(bounds.max, width));
}

const GroupedTabPenta = React.forwardRef<TabPentaRef, TabPentaProps>((props, ref) => {
  const data = props.data || EMPTY_DATA;
  const groups = groupTabItems(data, props.menuGroups || [], props.ungroupedLabel ?? "Ungrouped", {
    shortLabel: props.ungroupedShortLabel,
    icon: props.ungroupedIcon,
  });
  const [selectedKey, setSelectedKey] = useState(() => data[props.selectedTabIndex ?? 0]?.key || "");
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set());
  const [collapsed, setCollapsed] = useState(false);
  const [tutorialKey, setTutorialKey] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<MenuTooltip | null>(null);
  const [failedIconUrls, setFailedIconUrls] = useState<Set<string>>(() => new Set());
  const [containerWidth, setContainerWidth] = useState(0);
  const [preferredWidth, setPreferredWidth] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [resizeHovered, setResizeHovered] = useState(false);
  const [resizeFocused, setResizeFocused] = useState(false);
  const instanceId = useRef<number>();
  if (instanceId.current === undefined) instanceId.current = ++nextInstanceId;
  const previousProp = useRef(props.selectedTabIndex);
  const notified = useRef<{ key: string; index: number } | null>(null);
  const pending = useRef<{ key: string; callback: () => void } | null>(null);
  const scrollRequested = useRef(true);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; startWidth: number; element: HTMLDivElement } | null>(null);
  const hadMenuFocus = useRef(false);
  const dataKeySignature = JSON.stringify(data.map((item) => item.key));
  const previousDataKeySignature = useRef(dataKeySignature);

  const propChanged = previousProp.current !== props.selectedTabIndex;
  const propItem = propChanged && typeof props.selectedTabIndex === "number"
    ? data[props.selectedTabIndex] : undefined;
  const desiredKey = propItem?.key || selectedKey;
  const foundIndex = data.findIndex((item) => item.key === desiredKey);
  const activeIndex = foundIndex >= 0 ? foundIndex : data.length ? 0 : -1;
  const activeKey = data[activeIndex]?.key || "";
  const activeGroup = groups.find((group) => group.items.some(({ item }) => item.key === activeKey));
  const previousActiveKey = useRef(activeKey);
  const bounds = getMenuWidthBounds(containerWidth);
  const canResize = !!props.resizableMenu && !collapsed && containerWidth > 0 && bounds.max > bounds.min;
  const displayWidth = containerWidth > 0
    ? clampMenuWidth(preferredWidth ?? Math.min(360, containerWidth * 0.45), bounds)
    : null;

  const stopDragging = (releaseCapture = true) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (releaseCapture && drag.element.hasPointerCapture?.(drag.pointerId)) {
      drag.element.releasePointerCapture(drag.pointerId);
    }
    setDragging(false);
  };

  useLayoutEffect(() => {
    if (!props.resizableMenu || !containerRef.current) return;
    const container = containerRef.current;
    const measure = () => setContainerWidth(container.clientWidth);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(container);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [props.resizableMenu]);

  useEffect(() => {
    const onBlur = () => stopDragging();
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("blur", onBlur);
      const drag = dragRef.current;
      dragRef.current = null;
      if (drag?.element.hasPointerCapture?.(drag.pointerId)) drag.element.releasePointerCapture(drag.pointerId);
    };
  }, []);

  const scrollToKey = (key: string) => {
    const row = itemRefs.current[key];
    const menu = menuRef.current;
    if (!row || !menu) return;
    const rowRect = row.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    if (rowRect.top < menuRect.top) menu.scrollTop -= menuRect.top - rowRect.top;
    else if (rowRect.bottom > menuRect.bottom) menu.scrollTop += rowRect.bottom - menuRect.bottom;
  };

  const openGroup = (key: string) => {
    const group = groups.find((entry) => entry.items.some(({ item }) => item.key === key));
    if (group) setClosedGroups((previous) => {
      if (!previous.has(group.id)) return previous;
      const next = new Set(previous);
      next.delete(group.id);
      return next;
    });
  };

  useLayoutEffect(() => {
    previousProp.current = props.selectedTabIndex;
    if (selectedKey !== activeKey) setSelectedKey(activeKey);
    if (previousActiveKey.current !== activeKey || propChanged) {
      if (activeKey) openGroup(activeKey);
      // select() records its key in notified first, so this only catches prop and data-driven changes.
      if (activeKey && notified.current?.key !== activeKey) scrollRequested.current = true;
      previousActiveKey.current = activeKey;
    }
    if (previousDataKeySignature.current !== dataKeySignature) {
      // Menus added or removed can push the active row out of view without changing its key.
      if (!closedGroups.has(activeGroup?.id || "")) scrollRequested.current = true;
      previousDataKeySignature.current = dataKeySignature;
    }
    if (!activeKey) {
      notified.current = null;
      pending.current = null;
    } else if (notified.current?.key !== activeKey || notified.current?.index !== activeIndex) {
      notified.current = { key: activeKey, index: activeIndex };
      props.onChangeSelectedTabName?.(activeKey, activeIndex);
    }
    if (pending.current && !data.some((item) => item.key === pending.current?.key)) pending.current = null;
    if (scrollRequested.current && activeKey && !closedGroups.has(activeGroup?.id || "")) {
      scrollToKey(activeKey);
      scrollRequested.current = false;
    }
  });

  // Passive, like the flat path: the target page's effects and the ref handle are ready first.
  useEffect(() => {
    if (pending.current?.key === activeKey) {
      const callback = pending.current.callback;
      pending.current = null;
      callback();
    }
  });

  useEffect(() => {
    if (tutorialKey && !data.some((item) => item.key === tutorialKey)) setTutorialKey(null);
  }, [data, tutorialKey]);

  const tooltipContent = tooltip?.kind === "group"
    ? groups.find((group) => group.id === tooltip.key)?.label
    : data.find((item) => item.key === tooltip?.key)?.name;
  useEffect(() => {
    if (tooltip && (!collapsed || tooltipContent === undefined)) setTooltip(null);
  }, [collapsed, tooltip, tooltipContent]);

  useLayoutEffect(() => {
    if (hadMenuFocus.current && document.activeElement === document.body) menuRef.current?.focus();
    hadMenuFocus.current = false;
    return () => {
      hadMenuFocus.current = !!menuRef.current?.contains(document.activeElement);
    };
  }, [dataKeySignature]);

  const select = (index: number, userAction: boolean, callback?: () => void) => {
    const item = data[index];
    if (!item) return;
    if (callback) {
      if (item.key === activeKey) callback();
      else pending.current = { key: item.key, callback };
    } else if (pending.current && pending.current.key !== item.key) pending.current = null;
    openGroup(item.key);
    scrollRequested.current = !userAction;
    if (!userAction && item.key === activeKey && !closedGroups.has(activeGroup?.id || "")) {
      scrollToKey(item.key);
      scrollRequested.current = false;
    }
    // Set before the state update: outside a batch it commits synchronously and would notify again.
    notified.current = { key: item.key, index };
    setSelectedKey(item.key);
    props.onChangeSelectedTabName?.(item.key, index);
    if (userAction) {
      if (props.history) {
        const keys = props.history.location.pathname.split("/");
        const level = props.level ?? 0;
        if (keys.length > level) { keys[level] = item.key; keys.splice(level + 1); }
        else if (keys.length === level) keys.push(item.key);
        props.history.push(keys.join("/") + globalThis.location.search);
      }
      props.onTabChange?.(index);
    }
  };

  useImperativeHandle(ref, () => ({
    changeTab: (index) => select(index, false),
    changeTabKey: (key, callback) => select(data.findIndex((item) => item.key === key), false, callback),
  }));

  const showTooltip = (kind: MenuTooltip["kind"], key: string, node: HTMLElement) => {
    if (collapsed) {
      const rect = node.getBoundingClientRect();
      setTooltip({ kind, key, top: rect.top + rect.height / 2, left: rect.right + 4 });
    }
  };

  const renderItem = ({ item, sourceIndex }: IndexedItem) => {
    const active = item.key === activeKey;
    const title = textOf(item.name).trim() || item.key;
    const tutorial = props.tutorials?.[item.key] || [];
    const iconUrl = typeof item.icon === "string" ? item.icon : null;
    const icon = iconUrl && !failedIconUrls.has(iconUrl)
      ? <img src={iconUrl} alt="" onError={() => setFailedIconUrls((previous) => new Set(previous).add(iconUrl))}
          style={{ width: 20, height: 20, objectFit: "contain" }} />
      : item.icon && !iconUrl ? item.icon : title.charAt(0).toUpperCase();
    return (
      <div key={item.key} id={`tab-${item.key}`} ref={(node) => { itemRefs.current[item.key] = node; }}
        className={active ? "active" : ""}
        style={{ display: "flex", alignItems: "center", minHeight: 30, background: active ? "var(--primary-select-background)" : "white", ...props.itemStyle, ...(active ? props.activeStyle : {}) }}>
        <div role="button" tabIndex={props.disabled ? -1 : 0} aria-disabled={props.disabled || undefined}
          aria-current={active ? "page" : undefined} aria-label={collapsed ? title : undefined}
          aria-describedby={collapsed && tooltip?.kind === "item" && tooltip.key === item.key ? `tab-penta-tooltip-${instanceId.current}` : undefined}
          onMouseEnter={(event) => showTooltip("item", item.key, event.currentTarget)} onMouseLeave={() => setTooltip(null)}
          onFocus={(event) => showTooltip("item", item.key, event.currentTarget)} onBlur={() => setTooltip(null)}
          onClick={() => { if (!props.disabled) select(sourceIndex, true); }}
          onKeyDown={(event) => { if (!props.disabled && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); select(sourceIndex, true); } }}
          style={{ flex: 1, minWidth: 0, cursor: props.disabled ? "default" : "pointer", padding: collapsed ? "6px 2px" : "10px 14px 10px 27px", textAlign: collapsed ? "center" : "left", overflowWrap: "anywhere", color: active ? "black" : "var(--primary-theme-color-dark)", ...(active ? props.activeItemStyle : {}) }}>
          {collapsed ? (
            <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <span aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 20 }}>{icon}</span>
              <span style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", width: "100%", fontSize: "0.75rem", lineHeight: 1.25 }}>{title}</span>
            </span>
          ) : item.name}
        </div>
        {!collapsed && active && tutorial.length > 0 && (
          <button type="button" aria-label="Video tutorial" onClick={() => setTutorialKey(item.key)}
            style={{ border: 0, background: "transparent", cursor: "pointer", padding: "4px" }}>
            <img src="/static/images/menus/video-tutorial.png" alt="" style={{ width: 32 }} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div id="TabPenta" ref={containerRef} style={{ display: "flex", height: "100%", width: "100%", ...(dragging && { userSelect: "none" }) }}>
      <div id="TabPenta-Menu" ref={sidebarRef} style={{ display: "flex", flexDirection: "column", border: "1px solid #ccc", maxHeight: "95vh", flex: collapsed ? `0 0 ${COLLAPSED_MENU_WIDTH}px` : "0 1 360px", width: collapsed ? COLLAPSED_MENU_WIDTH : "32%", minWidth: 0, maxWidth: collapsed ? COLLAPSED_MENU_WIDTH : "45%", ...props.style, boxSizing: "border-box", ...(!collapsed && props.resizableMenu && displayWidth !== null && { flex: `0 0 ${displayWidth}px`, width: displayWidth, maxWidth: displayWidth }) }}>
        {props.collapsible && <button type="button" aria-label={collapsed ? "Expand menu" : "Collapse menu"} onClick={() => { stopDragging(); setResizeHovered(false); setResizeFocused(false); setCollapsed(!collapsed); }} style={{ textAlign: "left", padding: "6px 12px", border: 0, borderBottom: "1px solid #ccc", background: "white", color: "var(--primary-theme-color)", cursor: "pointer" }}><Icon name={collapsed ? "angle right" : "angle left"} /></button>}
        <div id={`tab-penta-menu-${instanceId.current}`} ref={menuRef} tabIndex={-1} style={{ flex: 1, overflowX: "hidden", overflowY: "auto" }}>
          {props.topTab}
          {groups.map((group) => {
            const open = !closedGroups.has(group.id);
            const panelId = `tab-penta-${instanceId.current}-${group.id}`;
            const isActiveGroup = activeGroup?.id === group.id;
            return <div key={group.id} style={{ borderBottom: "1px solid #dce2e5" }}>
              <button id={`${panelId}-header`} type="button" aria-label={groupAccessibleName(group, collapsed)} aria-expanded={open} aria-controls={panelId}
                aria-current={isActiveGroup ? "true" : undefined}
                aria-describedby={collapsed && tooltip?.kind === "group" && tooltip.key === group.id ? `tab-penta-tooltip-${instanceId.current}` : undefined}
                onMouseEnter={(event) => showTooltip("group", group.id, event.currentTarget)} onMouseLeave={() => setTooltip(null)}
                onFocus={(event) => showTooltip("group", group.id, event.currentTarget)} onBlur={() => setTooltip(null)}
                onClick={() => setClosedGroups((previous) => { const next = new Set(previous); if (next.has(group.id)) next.delete(group.id); else next.add(group.id); return next; })}
                style={{ width: "100%", display: "flex", flexDirection: collapsed ? "column" : "row", alignItems: collapsed ? "center" : "flex-start", gap: collapsed ? 4 : 8, padding: collapsed ? "10px 3px" : "14px 9px", border: 0, borderLeft: `3px solid ${isActiveGroup ? "var(--primary-theme-color)" : "transparent"}`, background: "#eef6f8", color: "#4c5c64", fontWeight: "bold", fontSize: collapsed ? "0.75rem" : "1.05rem", textAlign: collapsed ? "center" : "left", cursor: "pointer", overflowWrap: "anywhere" }}>
                <span aria-hidden="true" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {group.icon || <Icon name="folder outline" style={{ margin: 0 }} />}
                  {collapsed && <Icon name={open ? "caret down" : "caret right"} style={{ margin: 0 }} />}
                </span>
                <span style={{ flex: collapsed ? undefined : 1, minWidth: 0 }}>{collapsed ? group.shortLabel ?? group.label : group.label}</span>
                {!collapsed && <Icon name={open ? "caret down" : "caret right"} />}
              </button>
              <div id={panelId} role="group" aria-labelledby={`${panelId}-header`} hidden={!open} style={collapsed ? { margin: "6px 4px 8px 8px", paddingLeft: 4, borderLeft: "1px solid #dce2e5" } : undefined}>{group.items.map(renderItem)}</div>
            </div>;
          })}
        </div>
      </div>
      {props.resizableMenu && !collapsed && (
        <div role="separator" aria-label={props.resizeMenuLabel || "Resize menu"} aria-orientation="vertical"
          aria-controls={`tab-penta-menu-${instanceId.current}`} aria-valuemin={Math.round(bounds.min)}
          aria-valuemax={Math.round(bounds.max)} aria-valuenow={Math.round(displayWidth ?? 0)}
          aria-disabled={!canResize || undefined} tabIndex={canResize ? 0 : -1}
          onPointerEnter={() => setResizeHovered(true)} onPointerLeave={() => setResizeHovered(false)}
          onFocus={(event) => setResizeFocused(isKeyboardFocus(event.currentTarget))} onBlur={() => setResizeFocused(false)}
          onPointerDown={(event) => {
            if (!canResize || !event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
            const startWidth = sidebarRef.current?.getBoundingClientRect().width;
            if (startWidth === undefined) return;
            event.preventDefault();
            dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startWidth, element: event.currentTarget };
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
          }}
          onPointerMove={(event) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) return;
            const currentBounds = getMenuWidthBounds(containerRef.current?.clientWidth ?? containerWidth);
            setPreferredWidth(clampMenuWidth(drag.startWidth + event.clientX - drag.startX, currentBounds));
          }}
          onPointerUp={(event) => { if (dragRef.current?.pointerId === event.pointerId) stopDragging(); }}
          onPointerCancel={(event) => { if (dragRef.current?.pointerId === event.pointerId) stopDragging(); }}
          onLostPointerCapture={(event) => { if (dragRef.current?.pointerId === event.pointerId) stopDragging(false); }}
          onKeyDown={(event) => {
            if (!canResize || displayWidth === null) return;
            const step = event.shiftKey ? 50 : 10;
            let width: number;
            if (event.key === "ArrowLeft") width = displayWidth - step;
            else if (event.key === "ArrowRight") width = displayWidth + step;
            else if (event.key === "Home") width = bounds.min;
            else if (event.key === "End") width = bounds.max;
            else return;
            event.preventDefault();
            setResizeFocused(true);
            setPreferredWidth(clampMenuWidth(width, bounds));
          }}
          style={{ flex: `0 0 ${RESIZE_HANDLE_WIDTH}px`, boxSizing: "border-box", height: "min(100%, 95vh)", cursor: canResize ? "col-resize" : "default", touchAction: "none", background: dragging || resizeHovered || resizeFocused ? "var(--primary-theme-color)" : "#eef2f4", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc", outline: resizeFocused ? "2px solid var(--primary-theme-color-dark)" : undefined, outlineOffset: -2 }} />
      )}
      <TabPentaContent data={data} activeIndex={activeIndex} renderEmpty={props.renderEmpty} />
      {tutorialKey && data.some((item) => item.key === tutorialKey) && <ModalVideoTutorial videos={props.tutorials?.[tutorialKey] || []} open onClose={() => setTutorialKey(null)} />}
      {collapsed && tooltip && tooltipContent !== undefined && ReactDOM.createPortal(
        <div id={`tab-penta-tooltip-${instanceId.current}`} role="tooltip" style={{ position: "fixed", top: tooltip.top, left: tooltip.left, transform: "translateY(-50%)", zIndex: 9999, background: "white", border: "1px solid #ccc", borderRadius: 4, boxShadow: "2px 2px 6px rgba(0,0,0,0.15)", padding: "5px 10px", pointerEvents: "none" }}>
          {tooltipContent}
        </div>, document.body
      )}
    </div>
  );
});

GroupedTabPenta.displayName = "GroupedTabPenta";
export default GroupedTabPenta;
